import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../config/api';
import { PAYMENT_ROW_KEYS, PRICING_FALLBACK } from '../../constants/pricingFallback';
import './AdminPages.css';
import './AdminPricing.css';

const ROW_LABELS = {
  ebanking: 'E-banking / virement',
  twint: 'TWINT',
  revolut: 'Revolut',
  credit: 'Carte de crédit',
};

const emptyCotisation = () =>
  PAYMENT_ROW_KEYS.reduce((acc, k) => {
    acc[k] = {
      adultChf: '',
      adultEur: '',
      u14Chf: '',
      u14Eur: '',
      u5Chf: '',
      u5Eur: '',
    };
    return acc;
  }, {});

const emptyJuve = () =>
  PAYMENT_ROW_KEYS.reduce((acc, k) => {
    acc[k] = { chf: '', eur: '' };
    return acc;
  }, {});

function hydrateFromApi(data) {
  const c = emptyCotisation();
  const j = emptyJuve();
  PAYMENT_ROW_KEYS.forEach((k) => {
    const row = data.cotisation?.[k];
    if (row) {
      c[k] = {
        adultChf: row.adultChf ?? '',
        adultEur:
          row.adultEur === null || row.adultEur === undefined ? '' : row.adultEur,
        u14Chf: row.u14Chf ?? '',
        u14Eur: row.u14Eur === null || row.u14Eur === undefined ? '' : row.u14Eur,
        u5Chf: row.u5Chf ?? '',
        u5Eur: row.u5Eur === null || row.u5Eur === undefined ? '' : row.u5Eur,
      };
    }
    const jr = data.juventusCard?.[k];
    if (jr) {
      j[k] = {
        chf: jr.chf ?? '',
        eur: jr.eur === null || jr.eur === undefined ? '' : jr.eur,
      };
    }
  });
  return { cotisation: c, juventusCard: j };
}

function buildPayload(form) {
  const cotisation = {};
  const juventusCard = {};
  for (const k of PAYMENT_ROW_KEYS) {
    const r = form.cotisation[k];
    cotisation[k] = {
      adultChf: Number(r.adultChf),
      adultEur: r.adultEur === '' || r.adultEur === null ? null : Number(r.adultEur),
      u14Chf: Number(r.u14Chf),
      u14Eur: r.u14Eur === '' || r.u14Eur === null ? null : Number(r.u14Eur),
      u5Chf: Number(r.u5Chf),
      u5Eur: r.u5Eur === '' || r.u5Eur === null ? null : Number(r.u5Eur),
    };
    const j = form.juventusCard[k];
    juventusCard[k] = {
      chf: Number(j.chf),
      eur: j.eur === '' || j.eur === null ? null : Number(j.eur),
    };
  }
  return { cotisation, juventusCard };
}

const AdminPricing = () => {
  const [form, setForm] = useState(() => hydrateFromApi(PRICING_FALLBACK));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/settings/pricing');
      setForm(hydrateFromApi(data));
    } catch (e) {
      console.error('GET /settings/pricing failed, using local defaults:', e);
      setForm(hydrateFromApi(PRICING_FALLBACK));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setCoti = (key, field, value) => {
    setForm((prev) => ({
      ...prev,
      cotisation: {
        ...prev.cotisation,
        [key]: { ...prev.cotisation[key], [field]: value },
      },
    }));
  };

  const setJuve = (key, field, value) => {
    setForm((prev) => ({
      ...prev,
      juventusCard: {
        ...prev.juventusCard,
        [key]: { ...prev.juventusCard[key], [field]: value },
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = buildPayload(form);
    try {
      setSaving(true);
      const { data } = await api.put('/settings/pricing', payload);
      setForm(hydrateFromApi(data));
      alert('Tarifs enregistrés');
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message;
      alert(msg ? `Erreur : ${msg}` : 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page admin-pricing-page">
        <div className="admin-container">
          <p>Chargement des tarifs…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page admin-pricing-page">
      <div className="admin-container">
        <div className="admin-page-header">
          <div>
            <h1>Tarifs — Paiement et comptes</h1>
            <p className="admin-pricing-lead">
              Montants affichés sur la page publique{' '}
              <Link to="/paiement-et-comptes">/paiement-et-comptes</Link> (tableaux cotisation et Juventus Card).
            </p>
          </div>
          <Link to="/admin" className="btn-back">
            ← Tableau de bord
          </Link>
        </div>

        <form onSubmit={handleSubmit}>
          <section className="admin-pricing-block">
            <h2>Cotisation (JOFC Adult / Under 14 / Under 5)</h2>
            <div className="admin-table-container">
              <table className="admin-table admin-pricing-edit-table">
                <thead>
                  <tr>
                    <th>Mode de paiement</th>
                    <th colSpan={2}>JOFC Adult — CHF / EUR</th>
                    <th colSpan={2}>Under 14 — CHF / EUR</th>
                    <th colSpan={2}>Under 5 — CHF / EUR</th>
                  </tr>
                </thead>
                <tbody>
                  {PAYMENT_ROW_KEYS.map((k) => (
                    <tr key={k}>
                      <td>{ROW_LABELS[k]}</td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          className="admin-pricing-input"
                          value={form.cotisation[k].adultChf}
                          onChange={(e) => setCoti(k, 'adultChf', e.target.value)}
                          required
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          className="admin-pricing-input"
                          value={form.cotisation[k].adultEur}
                          onChange={(e) => setCoti(k, 'adultEur', e.target.value)}
                          placeholder="vide = —"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          className="admin-pricing-input"
                          value={form.cotisation[k].u14Chf}
                          onChange={(e) => setCoti(k, 'u14Chf', e.target.value)}
                          required
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          className="admin-pricing-input"
                          value={form.cotisation[k].u14Eur}
                          onChange={(e) => setCoti(k, 'u14Eur', e.target.value)}
                          placeholder="vide = —"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          className="admin-pricing-input"
                          value={form.cotisation[k].u5Chf}
                          onChange={(e) => setCoti(k, 'u5Chf', e.target.value)}
                          required
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          className="admin-pricing-input"
                          value={form.cotisation[k].u5Eur}
                          onChange={(e) => setCoti(k, 'u5Eur', e.target.value)}
                          placeholder="vide = —"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="admin-pricing-hint">
              Laisser EUR vide pour afficher « — » (ex. TWINT sans EUR). Under 5 : mettre 0 pour afficher « Gratuit » sur le site.
            </p>
          </section>

          <section className="admin-pricing-block">
            <h2>Juventus Card</h2>
            <div className="admin-table-container">
              <table className="admin-table admin-pricing-edit-table">
                <thead>
                  <tr>
                    <th>Mode de paiement</th>
                    <th>CHF</th>
                    <th>EUR</th>
                  </tr>
                </thead>
                <tbody>
                  {PAYMENT_ROW_KEYS.map((k) => (
                    <tr key={k}>
                      <td>{ROW_LABELS[k]}</td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          className="admin-pricing-input"
                          value={form.juventusCard[k].chf}
                          onChange={(e) => setJuve(k, 'chf', e.target.value)}
                          required
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          className="admin-pricing-input"
                          value={form.juventusCard[k].eur}
                          onChange={(e) => setJuve(k, 'eur', e.target.value)}
                          placeholder="vide = —"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <div className="admin-pricing-actions">
            <button type="submit" className="btn-add" disabled={saving}>
              {saving ? 'Enregistrement…' : 'Enregistrer les tarifs'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminPricing;
